import React from "react";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ShieldCheck,
  Landmark,
  Wallet,
  Building2,
  Trees,
  Plane,
  CheckCircle2,
} from "lucide-react";
import { useConsultation } from "../context/ConsultationContext";

const HERO_IMG =
  "https://images.pexels.com/photos/23229078/pexels-photo-23229078.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920";

const TRUST = [
  {
    icon: ShieldCheck,
    title: "100% Foreign Ownership",
    copy: "End-to-end structuring for full ownership across eligible activities.",
  },
  {
    icon: Landmark,
    title: "Direct Government Liaison",
    copy: "Former government officers on your file — no middlemen, no delays.",
  },
  {
    icon: Wallet,
    title: "Bank Account Opening",
    copy: "Relationship-led introductions to tier-1 UAE banks, structured for approval.",
  },
];

export default function Hero() {
  const { open } = useConsultation();

  return (
    <section
      data-testid="hero-section"
      className="relative isolate min-h-[92vh] flex items-end overflow-hidden bg-[#002366]"
    >
      {/* Background image */}
      <div className="absolute inset-0 -z-20">
        <img
          src={HERO_IMG}
          alt="Aerial view of Sheikh Zayed Road, Dubai"
          className="w-full h-full object-cover"
          loading="eager"
        />
      </div>
      {/* Navy gradient overlay */}
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-[#002366]/70 via-[#002366]/75 to-[#002366]" />
      {/* Gold accent line */}
      <div className="absolute top-24 left-6 md:left-12 h-px w-24 bg-[#3366FF]/80 hidden md:block" />

      <div className="container-x w-full pb-16 pt-40 md:pb-24 md:pt-48">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="flex items-center gap-3 mb-6"
          data-testid="hero-eyebrow"
        >
          <span className="chip-dark">
            <span className="w-1.5 h-1.5 rounded-full bg-[#6B8AFF] animate-pulse" />
            UAE Business Setup · Dubai · Abu Dhabi
          </span>
          <span className="hidden sm:inline-flex items-center gap-2 text-xs text-slate-300/75">
            <CheckCircle2 className="w-3.5 h-3.5 text-[#6B8AFF]" />
            500+ companies launched since 2016
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.05 }}
          className="font-heading font-light tracking-tight text-white text-balance text-4xl sm:text-5xl lg:text-7xl leading-[1.05] max-w-5xl"
          data-testid="hero-headline"
        >
          Empowering your vision in the{" "}
          <span className="font-black text-[#6B8AFF]">heart</span> of global trade.
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="mt-6 max-w-2xl text-slate-200/90 text-base md:text-lg leading-relaxed"
          data-testid="hero-subheadline"
        >
          StartINC is your premier partner for business setup in the UAE. From licensing
          to residency, we turn the complexity of incorporation into a seamless launchpad
          for your success.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.22 }}
          className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-slate-200/80"
        >
          <span className="inline-flex items-center gap-2">
            <Building2 className="w-4 h-4 text-[#6B8AFF]" /> Mainland LLC
          </span>
          <span className="w-px h-4 bg-white/15" />
          <span className="inline-flex items-center gap-2">
            <Trees className="w-4 h-4 text-[#6B8AFF]" /> 45+ Freezones
          </span>
          <span className="w-px h-4 bg-white/15" />
          <span className="inline-flex items-center gap-2">
            <Plane className="w-4 h-4 text-[#6B8AFF]" /> Golden Visa
          </span>
          <span className="w-px h-4 bg-white/15" />
          <span className="inline-flex items-center gap-2">
            <Wallet className="w-4 h-4 text-[#6B8AFF]" /> Corporate Banking
          </span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="mt-10 flex flex-col sm:flex-row gap-3"
        >
          <button
            data-testid="hero-cta-button"
            onClick={() => open("other")}
            className="btn-gold group"
          >
            Launch Your Business Today
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <a href="#mainland-freezone" data-testid="hero-secondary-cta" className="btn-outline-gold">
            Compare Mainland vs Freezone
          </a>
        </motion.div>

        {/* Trust bar */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.45 }}
          className="mt-20 md:mt-28 grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-white/15 border-t border-white/15"
          data-testid="hero-trust-bar"
        >
          {TRUST.map(({ icon: Icon, title, copy }) => (
            <div key={title} className="py-6 md:py-8 md:px-8 first:md:pl-0 flex gap-4">
              <div className="shrink-0 w-10 h-10 rounded-sm border border-[#6B8AFF]/50 flex items-center justify-center text-[#6B8AFF]">
                <Icon className="w-5 h-5" />
              </div>
              <div>
                <div className="font-heading font-semibold text-white">{title}</div>
                <div className="text-sm text-slate-300/80 mt-1 leading-relaxed">{copy}</div>
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
